import React, { useState, useEffect, useContext } from 'react';
import { FormControlLabel, Checkbox, TextField, InputAdornment, Avatar, Tooltip } from "@mui/material";
import { FaSearch } from "react-icons/fa";
import VerifiedIcon from '@mui/icons-material/Verified';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ContactsDetContext } from '../../../ContactsDetContext';
import { ReactComponent as NonVerifiedTickNew } from "../../../../Assets/img/icons/Non-verified-icon.svg";
import useApi from '../../../Utilities/useAPI';
import UserDetailContext, { useUserDetailContext } from '../../../UserDetailContext';

function ConnectedContacts() {
    const { groupDetails, setGroupDetails } = useContext(ContactsDetContext);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);  // Initialize with an empty array for item selection
    const [selectAll, setSelectAll] = useState(false);  // Do not select all by default
    const [ConnectedContacts, setConnectedContacts] = useState([]);
    const [unVerifiedCount, setUnVerifedCount] = useState([]);
    const [UnverifiedContacts, setUnverifiedContacts] = useState([]);
    const [favouriteContacts, setFavoriteContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState(ConnectedContacts);

    const { userDetails } = useUserDetailContext();

    // console.log("Ref ID:", userDetails?.reference_id);
    // console.log("Filtered:", filteredContacts?.map(m => m.contact_ref_id));

    const { callApiFunction } = useApi();
    const clearAllSelection = () => {
        setSelectedItems([]);
        setSelectAll(false); // Ensure select all is also reset
    };

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedItems([]);  // Deselect all when "Select All" is unchecked
        } else {
            const allContactIds = filteredContacts.map(member => member.contact_ref_id);
            setSelectedItems(allContactIds);  // Select all contacts
        }
        setSelectAll(!selectAll);  // Toggle the selectAll state
    };

    useEffect(() => {
        const fetchContacts = async () => {

            try {
                const response = await callApiFunction('ConnectedContacts');

                const data = response;
                if (data.statusCode === 200) {
                    const members = data.data.map(member => {
                        // Logic for role and subTitle
                        const role = member.designation || member.field_of_study || member.title || '---';  // Updated role logic
                        const subTitle = member.profession_department || member.institute || member.nickname || '---';  // New subTitle logic

                        return {
                            id: member.id,
                            contact_ref_id: member.contact_ref_id,
                            img: member.profile_url,  // Fallback to default image if profile URL is null
                            is_favourite: member?.is_favourite ? true : false,
                            name: `${member.first_name} ${member.last_name}`,
                            role: role,  // Based on new logic
                            email: `${member.first_name.toLowerCase()}.${member.last_name.toLowerCase()}@example.com`,  // Mock email as it's not provided
                            verified: member.is_verified ? true : false,
                            neoin_id: member.unique_id,
                            subTitle: subTitle  // Add new subTitle field
                        };
                    });
                    setConnectedContacts(members)

                    const unverifiedMembers = members.filter(member => !member.verified);
                    setUnverifiedContacts(unverifiedMembers);
                    setUnVerifedCount(unverifiedMembers.length);

                    // Filter and set favorite contacts
                    const favoriteMembers = members.filter(member => member.is_favourite);
                    setFavoriteContacts(favoriteMembers);
                }
            } catch (err) {
                console.log('An error occurred while sending the connection request:', err);
            }
        }
        fetchContacts();
    }, [])

    const handleItemSelection = (id) => {
        setSelectedItems(prevSelectedItems => {
            if (prevSelectedItems.includes(id)) {
                return prevSelectedItems.filter(item => item !== id);  // Remove the selected item
            } else {
                return [...prevSelectedItems, id];  // Add the selected item
            }
        });
    };

    useEffect(() => {
        setFilteredContacts(
            ConnectedContacts.filter(member =>
                member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.role.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, ConnectedContacts]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleNextClick = () => {
        // Step 1: Map selectedItems from filteredContacts
        const selectedMembers = filteredContacts
            .filter(contact => selectedItems.includes(contact.contact_ref_id)) // Only process selected contacts
            .map(member => ({
                contact_ref_id: member.contact_ref_id,
                img: member.img,  // Use default image if img is null
                name: member.name,
                role: member.role || 'Member',  // Default to 'Member' if role isn't available
                email: member.email || `${member.name.toLowerCase().replace(" ", ".")}@example.com`,  // Fallback email
                verified: member.verified  // Preserve verified status
            }));

        // Step 2: Merge existing members in groupDetailsEdit with selectedMembers
        const mergedMembers = [
            ...(groupDetails.members || []),
            ...selectedMembers
        ];

        // Step 3: Remove duplicate members based on contact_ref_id
        const uniqueMembers = mergedMembers.reduce((acc, current) => {
            if (!acc.some(member => member.contact_ref_id === current.contact_ref_id)) {
                acc.push(current);
            }
            return acc;
        }, []);

        // Step 4: Update the groupDetailsEdit with unique members
        setGroupDetails(prevDetails => ({
            ...prevDetails,
            members: uniqueMembers
        }));

        // Navigate to the next page
        navigate("/create_group");
    };

    return (
        <div className="outer-layer-of-contacts">
            <div className="outer-container">
                <div className="displayflex justify-content-between align-items-center">
                    <h6 className="fw-bold">All Contacts</h6>
                    <div className="flex-center">
                        <div className="">
                            <FormControlLabel
                                className='customized_formControl'
                                control={<Checkbox checked={selectAll} onChange={handleSelectAllChange} />}
                                label="Select All"
                            />
                        </div>
                        <div style={{ marginRight: '10px' }}>
                            <TextField
                                placeholder="Search..."
                                variant="outlined"
                                size="small"
                                className="me-2"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <div className="icon">
                                                <FaSearch className="icon-color" />
                                            </div>
                                        </InputAdornment>
                                    ),
                                }}
                                style={{ width: '320px' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="avatars-container">
                    {filteredContacts?.filter((member, index) => userDetails?.reference_id !== member?.contact_ref_id).map((member, index) => (
                        <div key={index} className={`avatar-box-outer compact`}>
                            <div className={`avatar-box t compact`}>
                                <div className="flex-center avatar-details-wrapper">
                                    <div className="dot_status_contacts">
                                        <Checkbox
                                            checked={selectedItems.includes(member.contact_ref_id)}
                                            onChange={() => handleItemSelection(member.contact_ref_id)}
                                            className="member-checkbox p-0"
                                        />
                                    </div>
                                    <div className="avatar-details t">
                                        <div>
                                            <div className="avatar-image t">
                                                <Avatar
                                                    className="w-100 h-100 cover-image"
                                                    src={member.img} alt={member.name}>
                                                    {member.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                            </div>
                                            <div className={`symbol_of_contacts compact`}>
                                                {member.verified ?
                                                    <VerifiedIcon style={{ color: "#43A047" }} /> :
                                                    <NonVerifiedTickNew />
                                                }
                                            </div>
                                        </div>
                                        <div className="member-info word-break">
                                            <Tooltip title={member.name} placement="bottom">
                                                <div className="member-name">
                                                    {member.name.length > 10
                                                        ? `${member.name.slice(0, 10)}...`
                                                        : member.name}
                                                </div>
                                            </Tooltip>
                                            <div className="member-role">{member.role}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selectedItems.length > 0 && (
                <div className={`action-bar action-barBg2 justify-content-end pe-3 ${selectedItems.length > 0 ? 'open' : ''}`}>
                    <Button className='me-3' variant='contained' onClick={clearAllSelection}
                        sx={{
                            backgroundColor: "#ffff",
                            color: "#0044c6",
                            '&:hover': {
                                backgroundColor: "#ffff",
                                color: "#0044c6",
                            }
                        }}>
                        CLEAR ALL
                    </Button>
                    <Button variant='contained' onClick={handleNextClick} sx={{ backgroundColor: "#0044C6" }} >
                        Next
                    </Button>
                </div>
            )}
        </div>
    );
}

export default ConnectedContacts;
